import { useEffect, useMemo, useState } from "react";
import { AnalyticsChartCard, StyledPieChart } from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";

const FILE_TYPE_CATEGORIES = [
    "Images",
    "PDFs",
    "CSV_Excel",
    "Text",
    "HTML",
    "Others",
];

const FILE_SIZE_BUCKETS = [
    "1_10_MB",
    "10_20_MB",
    "20_30_MB",
    "30_40_MB",
    "40_50_MB",
    "50_plus_MB",
];

const SIZE_BUCKET_LABELS = {
    "1_10_MB": "1-10 MB",
    "10_20_MB": "10-20 MB",
    "20_30_MB": "20-30 MB",
    "30_40_MB": "30-40 MB",
    "40_50_MB": "40-50 MB",
    "50_plus_MB": "50+ MB",
};

const PIE_COLORS = [
    "#6366F1",
    "#095023ff",
    "#F59E0B",
    "#EF4444",
    "#44cce4ff",
    "#b070ecff",
];




/**
 * Merge analytics arrays by date (for line chart)
 */
function mergeAnalyticsByDate(metrics) {
    const map = {};

    metrics.forEach(({ key, source, valueKey }) => {
        (source || []).forEach(item => {
            if (!map[item.date]) {
                map[item.date] = { date: item.date };
            }
            map[item.date][key] = item[valueKey] || 0;
        });
    });

    return Object.values(map).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
}

/**
 * Transform files_by_type → BarChart data
 */
function transformFilesByType(filesByType = {}) {
    return FILE_TYPE_CATEGORIES.map(category => ({
        name: category.replace("_", "/"),
        count: filesByType?.[category] ?? 0,
    }));
}

function transformSizeBucketToPieData(sizeBuckets = {}) {
    return FILE_SIZE_BUCKETS.map((bucket, index) => ({
        name: SIZE_BUCKET_LABELS[bucket],
        value: sizeBuckets?.[bucket] ?? 0,
        color: PIE_COLORS[index % PIE_COLORS.length],
    }));
}


/**
 * Metric config
 */
const METRIC_CONFIG = {
    files_uploaded_daywise: {
        source: "files",
        metricKey: "files_uploaded_daywise",
        chartKey: "files",
        valueKey: "total_files",
        label: "Files Uploaded",
        color: "#22C55E",
    },
};

const COLORS = {
    secondary: "#6366F1",
};

export default function FileAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
    } = useDashboardUI();

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError(false);

            const queryParams = buildAnalyticsQueryParams({
                startValue: selectedStartDateFilter,
                endValue: selectedEndDateFilter,
                facilityId: selectedFacilityFilter?.facility_number,
            });

            const res =
                await DashboardMetricsService.getFileUploadAnalytics(queryParams);

            setAnalytics({
                files: res.data,
            });
        } catch (err) {
            console.error("Failed to fetch file analytics", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter]);

    /**
     * Line chart: Files uploaded over time
     */
    const fileChart = useMemo(() => {
        if (!analytics) return { chartData: [], lines: [] };

        const config = METRIC_CONFIG.files_uploaded_daywise;
        const sourceData = analytics?.[config.source]?.[config.metricKey] || [];

        return {
            chartData: mergeAnalyticsByDate([
                {
                    key: config.chartKey,
                    source: sourceData,
                    valueKey: config.valueKey,
                },
            ]),
            lines: [
                {
                    dataKey: config.chartKey,
                    stroke: config.color,
                    name: config.label,
                },
            ],
        };
    }, [analytics]);

    /**
     * Bar chart: Files by type
     */
    const filesByTypeData = useMemo(() => {
        if (!analytics) return [];
        return transformFilesByType(
            analytics?.files?.files_by_type
        );
    }, [analytics]);

    const sizeBucketPieData = useMemo(() => {
        if (!analytics) return [];
        return transformSizeBucketToPieData(
            analytics?.files?.files_by_size_bucket
        );
    }, [analytics]);

    const sizeBucketTotal = useMemo(() => {
        return sizeBucketPieData.reduce((sum, d) => sum + d.value, 0);
    }, [sizeBucketPieData]);




    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading file analytics…
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-red-500">
                    Failed to load file analytics
                </div>
            </div>
        );
    }

    return (
        <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">

            <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Line Chart */}
                <AnalyticsChartCard
                    title="Upload Trends Over Time"
                    subtitle=""
                    {...fileChart}
                />

                {/* Bar Chart */}
                <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-900">Distribution by File Type</h2>
                    </div>

                    <div className="table-scroll overflow-x-auto lg:overflow-visible">
                    <div className="min-w-[520px] lg:min-w-0">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={filesByTypeData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                cursor={{ fill: '#f8fafc' }}
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                            />
                            <Bar
                                dataKey="count"
                                name="Files"
                                minPointSize={3}
                                fill={COLORS.secondary}
                                radius={[4, 4, 0, 0]}
                                barSize={40}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                    </div>
                    </div>
                </div>


                {/* Table for File size*/}
                <div className="min-w-0 overflow-hidden rounded-2xl border border-[#ECEEF4] bg-white lg:col-span-2">
                    <div className="px-5 pb-4 pt-5 sm:px-6">
                        <h2 className="text-base font-bold tracking-tight text-[#14122B]">Uploads by File Size</h2>
                    </div>

                    {sizeBucketTotal > 0 ? (
                        <>
                        <div className="table-scroll overflow-x-auto">
                            <table className="w-full min-w-[420px] border-collapse text-sm">
                                <thead>
                                    <tr className="border-y border-[#ECEEF4] bg-[#FAFAFD] text-[11px] font-semibold uppercase tracking-wide text-[#9AA1B2]">
                                        <th className="py-2.5 pl-5 pr-2 text-left sm:pl-6">Size Range</th>
                                        <th className="py-2.5 px-2 text-right">Files</th>
                                        <th className="py-2.5 pl-2 pr-5 text-right sm:pr-6">% of Total</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {sizeBucketPieData.map((item) => {
                                        const percentage = sizeBucketTotal
                                            ? Math.round((item.value / sizeBucketTotal) * 100)
                                            : 0;

                                        return (
                                            <tr key={item.name} className="border-b border-[#F1F2F7] last:border-b-0 hover:bg-gray-50 transition-colors">
                                                <td className="py-3 pl-5 pr-2 align-middle sm:pl-6">
                                                    <div className="flex items-center gap-2.5">
                                                        <span
                                                            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                                                            style={{ backgroundColor: item.color }}
                                                        />
                                                        <span className="text-sm font-bold text-[#14122B]">{item.name}</span>
                                                    </div>
                                                </td>

                                                <td className="py-3 px-2 text-right align-middle text-sm font-semibold text-[#14122B]">
                                                    {item.value}
                                                </td>

                                                <td className="py-3 pl-2 pr-5 text-right align-middle text-sm text-[#4B5266] sm:pr-6">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span>{percentage}%</span>
                                                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full"
                                                                style={{ width: `${percentage}%`, backgroundColor: item.color }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer sits outside the scrolling area so it stays put (no white gap) when the table scrolls */}
                        <div className="border-t border-[#ECEEF4] bg-[#FAFAFD] px-5 py-3 flex justify-end items-center text-sm sm:px-6">
                            <span className="text-[#9AA1B2] mr-2">Total Files Analyzed:</span>
                            <span className="font-bold text-[#14122B]">
                                {sizeBucketTotal}
                            </span>
                        </div>
                        </>
                    ) : (
                        <div className="mx-5 mb-5 flex items-center justify-center h-[200px] text-sm text-[#9AA1B2] border-2 border-dashed border-[#ECEEF4] rounded-xl sm:mx-6 sm:mb-6">
                            No file size data available in this time range
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
